import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../services/authService";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../styles/Addresses.css";

const emptyForm = {
  type: "SHIPPING",
  name: "",
  lastname1: "",
  lastname2: "",
  street: "",
  numberAddress: "",
  apartment: "",
  city: "",
  zipCode: "",
  province: "",
  country: "España",
  phone: "",
  predeterminated: false,

  taxId: "",
  companyName: ""
};

export default function Addresses() {
  const { t } = useTranslation();

  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const [showForm, setShowForm] = useState(false);
  const [sameForBilling, setSameForBilling] = useState(true);
  const shippingAddresses = addresses.filter((address) => address.type === "SHIPPING");
  const billingAddresses = addresses.filter((address) => address.type === "BILLING");
  const [sameBillingAddress, setSameBillingAddress] = useState(true);

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    const response = await fetch("/api/addresses", {
      credentials: "include",
    });

    if (response.ok) {
      setAddresses(await response.json());
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
    setShowForm(false);
    setSameForBilling(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const needsBillingData =
      form.type === "BILLING" ||
      (!editingId && form.type === "SHIPPING" && sameForBilling);

    if (needsBillingData && !form.taxId.trim()) {
      setError(t("addresses.taxIdRequired"));
      return;
    }

    const csrf = await getCsrfToken();

    const saveAddress = async (addressData, addressId = editingId) => {
      return fetch(
        addressId ? `/api/addresses/${addressId}` : "/api/addresses",
        {
          method: addressId ? "PUT" : "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrf.headerName]: csrf.token,
          },
          body: JSON.stringify(addressData),
        }
      );
    };

    const mainAddressData =
      form.type === "SHIPPING"
        ? {
            ...form,
            taxId: null,
            companyName: null,
          }
        : form;

    const response = await saveAddress(mainAddressData);

    if (!response.ok) {
      setError(t("addresses.error"));
      return;
    }

    if (!editingId && form.type === "SHIPPING" && sameForBilling) {
      const billingResponse = await saveAddress(
        {
          ...form,
          type: "BILLING",
          taxId: form.taxId.trim(),
          companyName: form.companyName.trim() || null,
        },
        null
      );

      if (!billingResponse.ok) {
        setError(t("addresses.error"));
        return;
      }
    }

    resetForm();
    await loadAddresses();
  };

  const handleEdit = (address) => {
    setEditingId(address.id);

    setForm({
      type: address.type || "SHIPPING",
      name: address.name || "",
      lastname1: address.lastname1 || "",
      lastname2: address.lastname2 || "",
      street: address.street || "",
      numberAddress: address.numberAddress || "",
      apartment: address.apartment || "",
      city: address.city || "",
      zipCode: address.zipCode || "",
      province: address.province || "",
      country: address.country || "",
      phone: address.phone || "",
      predeterminated: !!address.predeterminated,
      taxId: address.taxId || "",
      companyName: address.companyName || "",
    });

    setShowForm(true);
    setSameForBilling(false);
  };

  const handleDelete = async (id) => {
    const csrf = await getCsrfToken();

    await fetch(`/api/addresses/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    loadAddresses();
  };

  return (
    <main className="addresses-page">
      <section className="addresses-container">
        <button
          className="profile-back-btn"
          onClick={() => navigate(redirect ? redirect : "/profile")}
        >
          ← {redirect ? t("addresses.backToCheckout") : t("addresses.backToProfile")}
        </button>

        <h1>{t("addresses.title")}</h1>
        <p className="addresses-subtitle">{t("addresses.subtitle")}</p>

        <div className="addresses-layout">
          <div>
            {!showForm && (
              <button
                type="button"
                className="add-address-btn"
                onClick={() => setShowForm(true)}
              >
                + {t("addresses.addAddress")}
              </button>
            )}

            {showForm && (
              <form className="address-form" onSubmit={handleSubmit}>
                <h2>
                  {editingId
                    ? t("addresses.editAddress")
                    : t("addresses.newAddress")}
                </h2>

                <label>
                  {t("addresses.type")}
                  <select name="type" value={form.type} onChange={handleChange}>
                    <option value="SHIPPING">{t("addresses.shipping")}</option>
                    <option value="BILLING">{t("addresses.billing")}</option>
                  </select>
                </label>

                <label>
                  {t("addresses.name")}
                  <input name="name" value={form.name} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.lastname1")}
                  <input name="lastname1" value={form.lastname1} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.lastname2")}
                  <input name="lastname2" value={form.lastname2} onChange={handleChange} />
                </label>

                <label>
                  {t("addresses.street")}
                  <input name="street" value={form.street} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.numberAddress")}
                  <input name="numberAddress" value={form.numberAddress} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.apartment")}
                  <input name="apartment" value={form.apartment} onChange={handleChange} />
                </label>

                <label>
                  {t("addresses.city")}
                  <input name="city" value={form.city} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.zipCode")}
                  <input name="zipCode" value={form.zipCode} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.province")}
                  <input name="province" value={form.province} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.country")}
                  <input name="country" value={form.country} onChange={handleChange} required />
                </label>

                <label>
                  {t("addresses.phone")}
                  <input name="phone" value={form.phone} onChange={handleChange} required />
                </label>

                {(
                  form.type === "BILLING" ||
                  (!editingId && form.type === "SHIPPING" && sameForBilling)
                ) && (
                  <div className="address-fiscal-fields">
                    <h3>{t("addresses.billingDetails")}</h3>

                    <label>
                      {t("addresses.companyName")}
                      <input
                        type="text"
                        name="companyName"
                        value={form.companyName}
                        onChange={handleChange}
                        maxLength={255}
                      />
                    </label>

                    <label>
                      {t("addresses.taxId")}
                      <input
                        type="text"
                        name="taxId"
                        value={form.taxId}
                        onChange={handleChange}
                        required
                        maxLength={30}
                      />
                    </label>
                  </div>
                )}

                <label className="address-checkbox">
                  <input
                    type="checkbox"
                    name="predeterminated"
                    checked={form.predeterminated}
                    onChange={handleChange}
                  />
                  {t("addresses.predeterminated")}
                </label>

                {!editingId && form.type === "SHIPPING" && (
                  <label className="address-checkbox">
                    <input
                      type="checkbox"
                      checked={sameForBilling}
                      onChange={(e) => setSameForBilling(e.target.checked)}
                    />
                    {t("addresses.useAsBilling")}
                  </label>
                )}

                {error && <p className="login-error">{error}</p>}

                <div className="address-form-actions">
                  <button type="submit">
                    {editingId ? t("addresses.saveChanges") : t("addresses.save")}
                  </button>

                  <button type="button" onClick={resetForm}>
                    {t("addresses.cancel")}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="addresses-list">
            {addresses.length === 0 ? (
              <p className="addresses-empty">{t("addresses.empty")}</p>
            ) : (
              <>
                <h2 className="addresses-group-title">
                  {t("addresses.shippingAddresses")}
                </h2>

                {shippingAddresses.length === 0 ? (
                  <p className="addresses-empty-group">
                    {t("addresses.noShippingAddresses")}
                  </p>
                ) : (
                  shippingAddresses.map((address) => (
                    <article className="address-card" key={address.id}>
                      <div className="address-card-header">
                        <h3>{t("addresses.shipping")}</h3>

                        {address.predeterminated && (
                          <span>{t("addresses.defaultShipping")}</span>
                        )}
                      </div>

                      <p>
                        <strong>
                          {address.name} {address.lastname1}
                        </strong>
                      </p>

                      <p>
                        {address.street}, {address.numberAddress}
                      </p>

                      {address.apartment && <p>{address.apartment}</p>}

                      <p>
                        {address.zipCode} {address.city}
                        {address.province ? `, ${address.province}` : ""}
                      </p>

                      <p>{address.country}</p>

                      {address.phone && (
                        <p>
                          {t("addresses.phone")}: {address.phone}
                        </p>
                      )}

                      <div className="address-card-actions">
                        <button onClick={() => handleEdit(address)}>
                          {t("addresses.edit")}
                        </button>

                        <button onClick={() => handleDelete(address.id)}>
                          {t("addresses.delete")}
                        </button>
                      </div>
                    </article>
                  ))
                )}

                <label className="address-checkbox addresses-billing-toggle">
                  <input
                    type="checkbox"
                    checked={sameBillingAddress}
                    onChange={(e) => setSameBillingAddress(e.target.checked)}
                  />
                  {t("checkout.sameBillingAddress")}
                </label>

                {!sameBillingAddress && (
                  <>
                    <h2 className="addresses-group-title">
                      {t("addresses.billingAddresses")}
                    </h2>

                    {billingAddresses.length === 0 ? (
                      <p className="addresses-empty-group">
                        {t("addresses.noBillingAddresses")}
                      </p>
                    ) : (
                      billingAddresses.map((address) => (
                        <article className="address-card" key={address.id}>
                          <div className="address-card-header">
                            <h3>{t("addresses.billing")}</h3>

                            {address.predeterminated && (
                              <span>{t("addresses.defaultBilling")}</span>
                            )}
                          </div>

                          <p>
                            <strong>
                              {address.name} {address.lastname1}
                            </strong>
                          </p>

                          <p>
                            {address.street}, {address.numberAddress}
                          </p>

                          {address.apartment && <p>{address.apartment}</p>}

                          <p>
                            {address.zipCode} {address.city}
                            {address.province ? `, ${address.province}` : ""}
                          </p>

                          <p>{address.country}</p>

                          {address.phone && (
                            <p>
                              {t("addresses.phone")}: {address.phone}
                            </p>
                          )}

                          {address.companyName && (
                            <p>
                              <strong>{t("addresses.companyName")}:</strong>{" "}
                              {address.companyName}
                            </p>
                          )}

                          {address.taxId && (
                            <p>
                              <strong>{t("addresses.taxId")}:</strong>{" "}
                              {address.taxId}
                            </p>
                          )}

                          <div className="address-card-actions">
                            <button onClick={() => handleEdit(address)}>
                              {t("addresses.edit")}
                            </button>

                            <button onClick={() => handleDelete(address.id)}>
                              {t("addresses.delete")}
                            </button>
                          </div>
                        </article>
                      ))
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}